import axios from 'axios';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import DataTable from '../../components/table/DataTable';
import { useUser } from '../../context/UserContext';
import { Vocabulary } from '../../interfaces/vocab';
import { getPhoneticName, isPhoneticsSupported } from '../../utils/formatting';
import './VocabPage.css';

const PAGE_SIZE = 20;

const VocabPage: React.FC = () => {
  const { userData } = useUser();
  const languageCode = userData?.learning_language_code;
  const [vocabulary, setVocabulary] = useState<Vocabulary[]>([]);
  const [totalVocab, setTotalVocab] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const fetchVocabulary = useCallback(async () => {
    if (!languageCode) return;

    try {
      setLoading(true);
      const response = await axios.get(`/api/vocabulary/${languageCode}`, {
        params: { page, page_size: PAGE_SIZE, search: debouncedSearch },
      });
      setVocabulary(response.data.items);
      setTotalVocab(response.data.total);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
    } finally {
      setLoading(false);
    }
  }, [languageCode, page, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchVocabulary();
  }, [fetchVocabulary]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const headers = isPhoneticsSupported(languageCode || '')
    ? ['Word', getPhoneticName(languageCode || ''), 'Meaning', 'Times Seen', 'Last Seen']
    : ['Word', 'Meaning', 'Times Seen', 'Last Seen'];

  const rows = vocabulary.map((entry) => {
    const baseRow = [
      entry.word,
      entry.meaning,
      entry.seen_count,
      format(parseISO(entry.last_seen), 'MM/dd/yy'),
    ];

    return isPhoneticsSupported(languageCode || '')
      ? [entry.word, entry.phonetic || '', entry.meaning, entry.seen_count, format(parseISO(entry.last_seen), 'MM/dd/yy')]
      : baseRow;
  });

  const widths = isPhoneticsSupported(languageCode || '')
    ? ['auto', 'auto', 'auto', '15%', '18%']
    : ['auto', 'auto', '15%', '18%'];

  return (
    <div className="phn-page-container">
      <h1>Vocabulary</h1>
      <div className="phn-vertical-container">
        <input
          className="phn-input "
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <div className="vocab-data-table-container">
          <DataTable
            headers={headers}
            rows={rows}
            widths={widths}
            isSelectable={false}
            loading={loading}
            itemsPerPage={PAGE_SIZE}
            page={page}
            onPageChange={setPage}
            totalItems={totalVocab}
          />
        </div>
      </div>
    </div>
  );
};

export default VocabPage;
